import React, { useEffect } from "react";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  CircularProgress,
  Divider,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePostGooglePlacesAutocomplete } from "../api/use-post-google-places-autocomplete";
import { useSearchUsers } from "../api/use-search-users";
import { FollowButton } from "../components/Profile/FollowButton";
import { useFollowStore } from "../store/followStore";
import { useAuthStore } from "../store/authStore";

export const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const { profile } = useAuthStore();
  const { fetchFollowing } = useFollowStore();

  // Fetch search results
  const { data: placesData, isLoading: placesLoading } =
    usePostGooglePlacesAutocomplete({
      input: query,
    });

  const { data: usersData, isLoading: usersLoading } = useSearchUsers(query);

  // Fetch following list for current user
  useEffect(() => {
    if (profile?.id) {
      fetchFollowing(profile.id);
    }
  }, [profile?.id, fetchFollowing]);

  const places = placesData?.suggestions?.map((s) => s.placePrediction) || [];
  const users = usersData || [];

  const isLoading = placesLoading || usersLoading;
  const hasResults = places.length > 0 || users.length > 0;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "background.surface",
        paddingBottom: 4,
      }}
    >
      <Container maxWidth="md" sx={{ paddingTop: 2 }}>
        {/* Back Button */}
        <IconButton
          variant="outlined"
          size="sm"
          onClick={() => navigate(-1)}
          aria-label="back"
          sx={{ marginBottom: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Header */}
        <Typography level="h2" sx={{ marginBottom: 3 }}>
          Search Results
          {query && (
            <Typography level="body-md" sx={{ color: "text.secondary", mt: 1 }}>
              Showing results for "{query}"
            </Typography>
          )}
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : !hasResults ? (
          <Card variant="outlined" sx={{ padding: 4, textAlign: "center" }}>
            <Typography level="body-md" sx={{ color: "text.secondary" }}>
              No results found for "{query}"
            </Typography>
          </Card>
        ) : (
          <Stack spacing={4}>
            {/* People Section */}
            {users.length > 0 && (
              <Box>
                <Typography level="h4" sx={{ marginBottom: 2 }}>
                  People
                </Typography>
                <Stack spacing={2}>
                  {users.map((user) => (
                    <Card key={user.id} variant="outlined" data-testid={`user-card-${user.id}`}>
                      <CardContent>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            flex={1}
                            onClick={() => navigate(`/profile/${user.id}`)}
                            sx={{ cursor: "pointer" }}
                          >
                            <Avatar
                              src={user.avatar_url || undefined}
                              sx={{ width: 48, height: 48 }}
                            >
                              {user.username.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography level="title-md">
                                {user.full_name || user.username}
                              </Typography>
                              <Typography
                                level="body-sm"
                                sx={{ color: "text.secondary" }}
                              >
                                @{user.username}
                              </Typography>
                            </Box>
                          </Stack>
                          <FollowButton targetUserId={user.id} />
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Divider between sections */}
            {users.length > 0 && places.length > 0 && <Divider />}

            {/* Places Section */}
            {places.length > 0 && (
              <Box>
                <Typography level="h4" sx={{ marginBottom: 2 }}>
                  Places
                </Typography>
                <Stack spacing={2}>
                  {places.map((place, index) => (
                    <Card key={place.placeId || index} variant="outlined">
                      <CardContent>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            flex={1}
                          >
                            <LocationOnIcon
                              sx={{ color: "text.secondary", fontSize: 32 }}
                            />
                            <Box>
                              <Typography level="title-md">
                                {place.structuredFormat.mainText.text}
                              </Typography>
                              <Typography
                                level="body-sm"
                                sx={{ color: "text.secondary" }}
                              >
                                {place.structuredFormat.secondaryText.text}
                              </Typography>
                            </Box>
                          </Stack>
                          <Button
                            size="sm"
                            variant="outlined"
                            onClick={() => {
                              // Navigate to map with this place
                              navigate("/");
                              // Note: You might want to pass the place through state or a store
                            }}
                          >
                            View on Map
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Container>
    </Box>
  );
};
